import AdxFormModal from 'components/reusable/modal/AdxFormModal'
import { useQuery, useQueryClient } from 'react-query'
import React, { useState } from 'react'
import Box from '@mui/system/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import { useLocation, useNavigate } from 'react-router-dom'
import { handleView } from '../../report/functions'
import { deleteExpense, getExpense } from 'services/api/modules/claim/expense'
import AdxPopper from 'components/reusable/AdxPopper'
import WarningDeleteModal from 'module/claim/components/prompt-modal/WarningDeleteModal'
import EditExpenseModal from './EditExpenseModal'
import { parse } from 'date-fns'
import Loading from 'components/dumb/loading/Loading'
import { getReport } from 'services/api/modules/claim/report'
import ExpenseProvider from '../context/ExpenseContext'
import AdxButton from 'components/custom/AdxButton'

const SingleExpenseModal = () => {
  const navigate = useNavigate()
  const { pathname, state } = useLocation()
  const [isModalOpen, setModalOpen] = useState(true)
  const queryClient = useQueryClient()
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isEditModalOpen, setEditModalOpen] = useState(false)

  // segregate the report id and expense id from the param
  const reportId = pathname?.split('/')[pathname?.split('/').indexOf('reports') + 1]
  const expenseId = pathname?.split('/')[pathname.split('/').indexOf('expense') + 1]

  // API call
  const {
    isLoading: expenseLoading,
    // error,
    data,
  } = useQuery('singleExpenseData', async () => {
    const { data } = await getExpense({
      reportId: reportId,
      expenseId: expenseId,
    })
    return data
  })
  const { isLoading: reportLoading, data: reportData } = useQuery('singleReportData', async () => {
    const { data } = await getReport({ id: reportId })
    return data.report
  })
  const isLoading = () => {
    return [expenseLoading, reportLoading].some((item) => item === true)
  }

  const handleClose = async () => {
    queryClient.removeQueries('singleExpenseData', { exact: true })
    navigate(state.backgroundLocation)
    setModalOpen(false)
  }

  // prop custom header
  const ExpenseHeader = (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      columnGap={10}
      padding={2}
    >
      <Box>
        <Typography
          variant="h4"
          fontWeight="bold"
          color="dark-text.main"
          textTransform="capitalize"
        >
          {data?.expense_category?.name}
        </Typography>
      </Box>
      <Box display="flex" alignItems="center" columnGap={1}>
        <AdxPopper
          disablePortal
          linePlacement={[2]}
          placement="bottom-end"
          sx={{ width: 220, boxShadow: '0px 0px 10px lightgray' }}
        >
          <Typography variant="body1" color="dark-text.main" onClick={() => setEditModalOpen(true)}>
            Edit expense
          </Typography>
          <Typography
            variant="body1"
            color="dark-text.main"
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete expense
          </Typography>
        </AdxPopper>

        {/* EDIT EXPENSE MODAL */}
        <ExpenseProvider>
          <EditExpenseModal
            isEditModalOpen={isEditModalOpen}
            setEditModalOpen={setEditModalOpen}
            reportData={reportData}
            expenseData={{
              id: data?.id,
              date: parse(data?.date, 'dd/MM/yyyy', new Date()),
              merchant: data?.merchant,
              distance: data?.distance,
              description: data?.description,
              amount: parseFloat(data?.amount).toFixed(2),
              proof_link: data?.proof_link,
              file: { name: data?.attachment, url: data?.attachment_url },
            }}
            previousSelectedCategory={data?.expense_category}
          />
        </ExpenseProvider>

        {/* DELETE EXPENSE MODAL */}
        <WarningDeleteModal
          id={data?.id}
          deleteAPI={() => deleteExpense(data?.claim_report_id, data?.id)}
          reactQueryKeys={['singleReportData']}
          title={`Delete ${data?.category} expense for ${data?.merchant}`}
          subtitle="You will lose all data under this expense."
          isOpen={isDeleteModalOpen}
          closeModalFunction={setDeleteModalOpen}
          successFunction={handleClose}
        />
        <Box display="flex" alignItems="center" onClick={handleClose} sx={{ cursor: 'pointer' }}>
          <Icon sx={{ color: 'dark-text.main' }}>close_rounded</Icon>
        </Box>
      </Box>
    </Box>
  )

  // styling
  const row = { width: 1, display: 'flex', paddingY: 1, alignItems: 'start' }
  const column1 = { width: 120, color: 'light-gray-text.main' }
  const column2 = { width: 'calc(100% - 120px)', color: 'dark-text.main' }

  return isLoading() ? (
    <Loading show />
  ) : (
    <AdxFormModal
      open={isModalOpen}
      onClose={handleClose}
      customHeader={ExpenseHeader}
      disableAutoFocus
    >
      <Box mb={3}>
        <Box sx={row}>
          <Box sx={column1}>
            <Typography variant="body1">Category</Typography>
          </Box>
          <Box sx={column2}>
            <Typography textTransform="capitalize" variant="body1">
              {data?.expense_category?.name}
            </Typography>
          </Box>
        </Box>
        {data?.merchant && (
          <Box sx={row}>
            <Box sx={column1}>
              <Typography variant="body1">Merchant</Typography>
            </Box>

            <Box sx={column2}>
              <Typography variant="body1">{data?.merchant}</Typography>
            </Box>
          </Box>
        )}
        {data?.distance && (
          <Box sx={row}>
            <Box sx={column1}>
              <Typography variant="body1">Distance</Typography>
            </Box>

            <Box sx={column2}>
              <Typography variant="body1">{data?.distance} km</Typography>
            </Box>
          </Box>
        )}
        <Box sx={row}>
          <Box sx={column1}>
            <Typography variant="body1">Receipt date</Typography>
          </Box>
          <Box sx={column2}>
            <Typography variant="body1">{data?.date}</Typography>
          </Box>
        </Box>
        <Box sx={row}>
          <Box sx={column1}>
            <Typography variant="body1">Amount</Typography>
          </Box>
          <Box sx={column2}>
            <Typography variant="body1" fontWeight="bold" color="alternate.main">
              RM {parseFloat(data?.amount).toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box mb={3}>
        <Typography variant="body1" color="light-gray-text.main" mb={2}>
          Description
        </Typography>
        <Typography variant="body1" color="dark-text.main">
          {data?.description}
        </Typography>
      </Box>
      <Box>
        <Typography variant="body1" color="light-gray-text.main" mb={2}>
          Attachment
        </Typography>
        <Box>
          <Box
            sx={{
              minWidth: '250px',
              height: '100%',
              width: '100%',
              borderRadius: 4,
              border: '3px solid',
              borderColor: 'gray-line.main',

              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            {/* This this later */}
            <Box
              as="img"
              src={data?.attachment_url}
              width="100%"
              height="200px"
              borderRadius="12px 12px 0 0"
              display="flex"
              justifyContent="center"
              alignItems="center"
              sx={{
                objectFit: 'cover',
                objectPosition: '0% 0%',
              }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',

                p: 2,
              }}
            >
              <Box display="flex" flexDirection="column" rowGap={1}>
                <Typography
                  noWrap
                  width="calc(100vw - 270px)"
                  variant="body1"
                  sx={{ fontWeight: 'bold' }}
                >
                  {data?.attachment}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" columnGap={1}>
                <AdxButton
                  variant={'ghost'}
                  onClick={() => handleView(data?.attachment_url)}
                  size="small"
                  sx={{ textDecoration: 'underline' }}
                >
                  View
                </AdxButton>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </AdxFormModal>
  )
}

export default SingleExpenseModal
